import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { EstabelecimentosService } from '../../../estabelecimentos/system-admin/Service';
import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { useAppContext } from '../../../../contexts/AppContext';
import { Horarios, initialHorarios } from '../../Enum';
import { AtendentesService } from '../../../atendentes/Service';
import { useFilterApp } from '../../../../contexts/FilterApp';
import { useAuthContext } from '../../../../contexts/AuthContext';

const serviceEstab = new EstabelecimentosService();
const atendentesService = new AtendentesService();

interface AgendaSettingsContextProps {
    daysOpen: number;
    setDaysOpen: React.Dispatch<React.SetStateAction<number>>;
    horarios: Horarios;
    setHorarios: React.Dispatch<React.SetStateAction<Horarios>>;
    requireConfirmation: boolean;
    gapBetweenSlots: number; // ✅ NOVO
    setGapBetweenSlots: React.Dispatch<React.SetStateAction<number>>; // ✅ NOVO
    setRequireConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
    minutesBeforeConfirmation: number;
    setMinutesBeforeConfirmation: React.Dispatch<React.SetStateAction<number>>;
    sendWhatsapp: boolean;
    setSendWhatsapp: React.Dispatch<React.SetStateAction<boolean>>;
    scheduleGap: number;
    setScheduleGap: React.Dispatch<React.SetStateAction<number>>;
    minHour: number;
    setMinHour: React.Dispatch<React.SetStateAction<number>>;
    maxHour: number;
    setMaxHour: React.Dispatch<React.SetStateAction<number>>;
    minAdvanceTime: number;
    subscriptionOnlyScheduling: boolean;
    setSubscriptionOnlyScheduling: React.Dispatch<React.SetStateAction<boolean>>;
    subscriptionServicesOnly: boolean;
    setSubscriptionServicesOnly: React.Dispatch<React.SetStateAction<boolean>>;
    maxScheduleDate: string | null;
    setMaxScheduleDate: React.Dispatch<React.SetStateAction<string | null>>;
    setMinAdvanceTime: React.Dispatch<React.SetStateAction<number>>;
    minutesBeforeCancellation: number;
    setMinutesBeforeCancellation: React.Dispatch<React.SetStateAction<number>>;
    allowCancellation: boolean;
    setAllowCancellation: React.Dispatch<React.SetStateAction<boolean>>;
    canEditAgendaSettings: boolean;
    canEditEstablishmentAgendaSettings: boolean;
    submitGeral: () => void;
    submitHorarios: () => void;
    submitAdm: () => void;
    loading: boolean;
}

const validateHorarios = (horarios: Horarios): Horarios => {
    const validatedHorarios: Horarios = {};
    for (const [day, slots] of Object.entries(horarios)) {
        validatedHorarios[day] = slots.filter((slot) => slot.start > 0 && slot.end > 0 && slot.start < slot.end);
    }
    return validatedHorarios;
};

const AgendaSettingsContext = createContext<AgendaSettingsContextProps | undefined>(undefined);

const AgendaSettingsProvider: React.FC<{ children: ReactNode; attendantId?: string }> = ({ children, attendantId }) => {
    const { errorSnackbar, successSnackbar } = useAppContext();
    const { user } = useAuthContext();
    const { estabSession, reloadEstab } = useEstabsContext();
    const { attendantId: selectedAttendantId } = useFilterApp();
    const atendenteId = attendantId || selectedAttendantId;
    const canEditAgendaSettings = !!atendenteId;
    const canEditEstablishmentAgendaSettings = Boolean(user?.root || user?.admin);

    const [maxScheduleDate, setMaxScheduleDate] = useState<string | null>(null);

    const [gapBetweenSlots, setGapBetweenSlots] = useState(0);
    const [allowCancellation, setAllowCancellation] = useState(false);
    const [minutesBeforeCancellation, setMinutesBeforeCancellation] = useState(60);
    const [loading, setLoading] = useState(false);
    const [daysOpen, setDaysOpen] = useState(10);
    const [horarios, setHorarios] = useState<Horarios>(initialHorarios);
    const [requireConfirmation, setRequireConfirmation] = useState(false);
    const [minutesBeforeConfirmation, setMinutesBeforeConfirmation] = useState(60);
    const [sendWhatsapp, setSendWhatsapp] = useState(false);
    const [scheduleGap, setScheduleGap] = useState(10);
    const [minHour, setMinHour] = useState(6 * 60);
    const [maxHour, setMaxHour] = useState(24 * 60);
    const [minAdvanceTime, setMinAdvanceTime] = useState(30);
    const [subscriptionOnlyScheduling, setSubscriptionOnlyScheduling] = useState(false);
    const [subscriptionServicesOnly, setSubscriptionServicesOnly] = useState(false);

    const submitGeral = async () => {
        if (!canEditAgendaSettings) {
            errorSnackbar('Selecione um atendente para editar a agenda.');
            return;
        }

        try {
            setLoading(true);

            if (
                !Number.isInteger(minHour) ||
                !Number.isInteger(maxHour) ||
                minHour < 0 ||
                maxHour > 24 * 60 ||
                minHour >= maxHour
            ) {
                errorSnackbar('Defina uma faixa válida entre 00:00 e 24:00.');
                return;
            }

            const payload = {
                'agenda.daysOpen': daysOpen,
                'agenda.gapBetweenSlots': Math.max(0, gapBetweenSlots), // ✅ NOVO
                'agenda.scheduleGap': Math.max(10, scheduleGap),
                'agenda.minAdvanceTime': Math.max(0, minAdvanceTime),
                'agenda.maxScheduleDate': maxScheduleDate || null,
                'agenda.subscriptionOnlyScheduling': subscriptionOnlyScheduling,
                'agenda.subscriptionServicesOnly': subscriptionServicesOnly,
            };

            if (!!atendenteId) {
                await atendentesService.updateAgenda(atendenteId, payload);
            } else if (canEditEstablishmentAgendaSettings) {
                await serviceEstab.update(estabSession._id, payload);
            }

            if (canEditEstablishmentAgendaSettings) {
                await serviceEstab.update(estabSession._id, {
                    'agenda.minHour': minHour,
                    'agenda.maxHour': maxHour,
                });
            }

            successSnackbar('Agenda editada com sucesso!');
            reloadEstab();
        } catch (e) {
            errorSnackbar('Ocorreu um erro ao editar a agenda.');
        } finally {
            setLoading(false);
        }
    };

    const submitHorarios = async () => {
        if (!canEditAgendaSettings) {
            errorSnackbar('Selecione um atendente para editar a agenda.');
            return;
        }

        try {
            setLoading(true);
            const validatedHorarios = validateHorarios(horarios);

            if (!!atendenteId) {
                await atendentesService.updateAgenda(atendenteId, {
                    'agenda.horarios': validatedHorarios,
                });
            } else {
                await serviceEstab.update(estabSession._id, {
                    'agenda.horarios': validatedHorarios,
                });
            }

            successSnackbar('Agenda editada com sucesso!');
            reloadEstab();
        } catch (e) {
            errorSnackbar('Ocorreu um erro ao editar a agenda.');
        } finally {
            setLoading(false);
        }
    };

    const submitAdm = async () => {
        if (!canEditAgendaSettings) {
            errorSnackbar('Selecione um atendente para editar a agenda.');
            return;
        }

        try {
            setLoading(true);
            await serviceEstab.update(estabSession._id, {
                'agenda.sendWhatsapp': sendWhatsapp,
                'agenda.allowCancellation': allowCancellation,
                'agenda.minutesBeforeCancellation': minutesBeforeCancellation,
                'agenda.requireConfirmation': requireConfirmation,
                'agenda.minutesBeforeConfirmation': minutesBeforeConfirmation,
            });

            successSnackbar('Agenda editada com sucesso!');
            reloadEstab();
        } catch (e) {
            errorSnackbar('Ocorreu um erro ao editar a agenda.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            if (!estabSession) return;

            const normalizeToDateInput = (value?: string | Date | null) => {
                if (!value) return null;
                const d = new Date(value);
                if (Number.isNaN(d.getTime())) return null;
                return d.toISOString().slice(0, 10);
            };

            const doc = {
                ...initialHorarios,
                ...(estabSession.agenda?.horarios || {}),
            };
            delete doc._id;

            const defaultAgenda = {
                daysOpen: estabSession.agenda?.daysOpen || 10,
                gapBetweenSlots: estabSession.agenda?.gapBetweenSlots ?? 0,
                scheduleGap: estabSession.agenda?.scheduleGap || 10,
                minAdvanceTime: estabSession.agenda?.minAdvanceTime || 0,
                minHour: 6 * 60,
                maxHour: 24 * 60,
                horarios: doc,
                maxScheduleDate: normalizeToDateInput(estabSession.agenda?.maxScheduleDate ?? null),
                subscriptionOnlyScheduling: Boolean(estabSession.agenda?.subscriptionOnlyScheduling),
                subscriptionServicesOnly: Boolean(estabSession.agenda?.subscriptionServicesOnly),
            };
            const persistedMinHour = Number(estabSession.agenda?.minHour);
            const persistedMaxHour = Number(estabSession.agenda?.maxHour);
            if (
                Number.isInteger(persistedMinHour) &&
                Number.isInteger(persistedMaxHour) &&
                persistedMinHour >= 0 &&
                persistedMaxHour <= 24 * 60 &&
                persistedMinHour < persistedMaxHour
            ) {
                defaultAgenda.minHour = persistedMinHour;
                defaultAgenda.maxHour = persistedMaxHour;
            }

            if (atendenteId) {
                try {
                    setLoading(true);
                    const { data } = await atendentesService.getAgenda(atendenteId);

                    setDaysOpen(data?.daysOpen ?? defaultAgenda.daysOpen);
                    setScheduleGap(data?.scheduleGap ?? defaultAgenda.scheduleGap);
                    setGapBetweenSlots(data?.gapBetweenSlots ?? defaultAgenda.gapBetweenSlots);
                    setMinAdvanceTime(data?.minAdvanceTime ?? defaultAgenda.minAdvanceTime);
                    setHorarios(data?.horarios ?? defaultAgenda.horarios);
                    setSubscriptionOnlyScheduling(
                        Boolean(data?.subscriptionOnlyScheduling ?? defaultAgenda.subscriptionOnlyScheduling)
                    );
                    setSubscriptionServicesOnly(
                        Boolean(data?.subscriptionServicesOnly ?? defaultAgenda.subscriptionServicesOnly)
                    );
                    setMaxScheduleDate(
                        normalizeToDateInput(data?.maxScheduleDate ?? null) ?? defaultAgenda.maxScheduleDate
                    );
                } catch {
                    setDaysOpen(defaultAgenda.daysOpen);
                    setScheduleGap(defaultAgenda.scheduleGap);
                    setMinAdvanceTime(defaultAgenda.minAdvanceTime);
                    setGapBetweenSlots(defaultAgenda.gapBetweenSlots);
                    setHorarios(defaultAgenda.horarios);
                    setSubscriptionOnlyScheduling(defaultAgenda.subscriptionOnlyScheduling);
                    setSubscriptionServicesOnly(defaultAgenda.subscriptionServicesOnly);
                    setMaxScheduleDate(defaultAgenda.maxScheduleDate);
                } finally {
                    setLoading(false);
                }
            } else {
                setDaysOpen(defaultAgenda.daysOpen);
                setScheduleGap(defaultAgenda.scheduleGap);
                setGapBetweenSlots(defaultAgenda.gapBetweenSlots);
                setMinAdvanceTime(defaultAgenda.minAdvanceTime);
                setHorarios(defaultAgenda.horarios);
                setSubscriptionOnlyScheduling(defaultAgenda.subscriptionOnlyScheduling);
                setSubscriptionServicesOnly(defaultAgenda.subscriptionServicesOnly);
                setMaxScheduleDate(defaultAgenda.maxScheduleDate);
            }

            setMinutesBeforeConfirmation(estabSession.agenda?.minutesBeforeConfirmation ?? 60);
            setMinHour(defaultAgenda.minHour);
            setMaxHour(defaultAgenda.maxHour);
            setAllowCancellation(estabSession.agenda?.allowCancellation ?? false);
            setMinutesBeforeCancellation(estabSession.agenda?.minutesBeforeCancellation ?? 0);
            setRequireConfirmation(estabSession.agenda?.requireConfirmation ?? false);
            setSendWhatsapp(estabSession.agenda?.sendWhatsapp ?? false);
        };

        load();
    }, [estabSession, atendenteId]);

    return (
        <AgendaSettingsContext.Provider
            value={{
                daysOpen,
                setDaysOpen,
                horarios,
                setHorarios,
                requireConfirmation,
                setRequireConfirmation,
                minutesBeforeConfirmation,
                setMinutesBeforeConfirmation,
                sendWhatsapp,
                setSendWhatsapp,
                gapBetweenSlots,
                setGapBetweenSlots,
                submitAdm,
                submitHorarios,
                maxScheduleDate,
                setMaxScheduleDate,
                submitGeral,
                loading,
                scheduleGap,
                setScheduleGap,
                minHour,
                setMinHour,
                maxHour,
                setMaxHour,
                minAdvanceTime,
                setMinAdvanceTime,
                subscriptionOnlyScheduling,
                setSubscriptionOnlyScheduling,
                subscriptionServicesOnly,
                setSubscriptionServicesOnly,
                canEditAgendaSettings,
                canEditEstablishmentAgendaSettings,
                allowCancellation,
                setAllowCancellation,
                minutesBeforeCancellation,
                setMinutesBeforeCancellation,
            }}
        >
            {children}
        </AgendaSettingsContext.Provider>
    );
};

const useAgendaSettingsContext = () => {
    const context = useContext(AgendaSettingsContext);
    if (context === undefined) {
        throw new Error('useAgendaSettingsContext must be used within an AgendaSettingsProvider');
    }
    return context;
};

export { AgendaSettingsProvider, useAgendaSettingsContext };
